import React, { useState } from "react";
import { Button, Menu, MenuItem, Checkbox, ListItemText, Divider, Box, Chip, Stack } from "@mui/material";
import { PlusCircle } from "@phosphor-icons/react";

export function MultiSelectFilter({ value, onChange, options, label = "Filter" }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (optionValue, isClear = false) => {
    const currentValues = Array.isArray(value) ? value : [];
    let newValues;

    if (optionValue === "" && !isClear) {
      // Handle "All" selection
      if (currentValues.length === 0 || !currentValues.includes("")) {
        newValues = ["", ...options.filter((o) => o.value !== "").map((o) => o.value)];
      } else {
        newValues = [];
      }
    } else if (optionValue === "" && isClear) {
      // Handle "Clear selection" to set empty array
      newValues = [];
    } else {
      // Handle individual option selection
      newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues.filter((v) => v !== ""), optionValue];

      // Remove "" if any individual option is deselected
      if (currentValues.includes(optionValue) && currentValues.includes("")) {
        newValues = newValues.filter((v) => v !== "");
      }

      // If all options (except "") are selected, automatically include "All" with ""
      const allValuesExceptEmpty = options.filter((o) => o.value !== "").map((o) => o.value);
      if (newValues.length === allValuesExceptEmpty.length && newValues.every((v) => allValuesExceptEmpty.includes(v))) {
        newValues = ["", ...allValuesExceptEmpty];
      }
    }
    onChange(newValues);
  };

  const displayValue = () => {
    if (!value || value.length === 0) return null;

    if (value.includes("")) {
      return (
        <Chip
          label="All"
          variant="filled"
          sx={{
            height: 24,
            fontSize: 12,
            lineHeight: "24px",
            p: 0,
          }}
        />
      );
    }

    return (
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {value.map((v) => {
          const option = options.find((o) => o.value === v);
          const label = option ? option.label : v;
          return (
            <Chip
              key={v}
              label={label}
              variant="filled"
              sx={{
                height: 24,
                fontSize: 12,
                lineHeight: "24px",
                borderRadius: 1,
              }}
            />
          );
        })}
      </Stack>
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<PlusCircle />}
        sx={{
          borderRadius: 1,
          border: "1px dashed",
          borderColor: "grey.light",
          textTransform: "none",
          minWidth: 100,
          display: "flex",
          alignItems: "center",
          py: 0,
          pl: 1.5,
          pr: 1,
          color: "black",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", height: 30 }}>
          <Box sx={{ flexGrow: 1, textAlign: "left" }}>{label}</Box>
          {displayValue() && <Divider orientation="vertical" flexItem sx={{ height: "100%", mx: 1 }} />}
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1, textAlign: "left", gap: 1, flexWrap: "wrap" }}>
            {displayValue()}
          </Box>
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          list: {
            "aria-labelledby": "filter-button",
            sx: {
              padding: "4px",
              width: "200px",
            },
          },
        }}
      >
        {[{ value: "", label: "All" }, ...options].map((option) => (
          <MenuItem key={option.value} onClick={() => handleSelect(option.value)} sx={{ py: 0.5, px: 1.5, borderRadius: 1 }}>
            <Checkbox checked={Array.isArray(value) && value.includes(option.value)} color="primary" sx={{ padding: 0.5 }} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
        <Divider sx={{ m: "0px !important" }} />
        <MenuItem
          onClick={() => handleSelect("", true)}
          sx={{
            border: 0,
            borderRadius: 1,
            color: "text.secondary",
            justifyContent: "center",
            textAlign: "center",
            width: "100%",
            fontStyle: "italic",
            py: 1,
          }}
        >
          Clear selection
        </MenuItem>
      </Menu>
    </>
  );
}
